import { useState, type FormEvent } from "react";
import {
  EREIGNISARTEN,
  EREIGNIS_LABEL,
  EREIGNISROLLEN,
  EREIGNISROLLE_LABEL,
  FAMILIEN_EREIGNISARTEN,
  parseDatum,
  alsEingabetext,
  verweisAufbereiten,
  type VerweisZeile,
  type Ereignisart,
  type Ereignisrolle,
  type UnscharfesDatum,
} from "@stammbaum/shared";
import { api } from "../api";
import { DatumFeld } from "./DatumFeld";
import { PersonSucher } from "./PersonSucher";
import { OrtFeld } from "./OrtFeld";

/**
 * Maske für ein Ereignis.
 *
 * Die wichtigste Entscheidung darin: Hochzeit, Verpartnerung, Scheidung und
 * Jubiläum betreffen immer zwei Menschen. Sie hängen deshalb nicht an einer
 * Person, sondern an der Familie — damit das Hochzeitsfoto beiden gehört und
 * nicht doppelt eingetragen werden muss. Wählt man so eine Art, fragt die
 * Maske nach der Familie und trägt beide Partner automatisch ein.
 */

export interface Beteiligter {
  personId: number;
  name: string;
  rolle: Ereignisrolle;
}

export interface EreignisWerte {
  id?: number;
  art: Ereignisart;
  datumText: string;
  ortName: string;
  /**
   * Der zuletzt gespeicherte Ort. Gewinnt beim Speichern, solange der Text
   * unverändert ist: die Maske zeigt nur die unterste Ebene, und aus
   * „Krems" allein liesse sich sonst ein anderer Krems in der Kette
   * heraussuchen als der, an dem das Ereignis hängt.
   */
  ortId: number | null;
  familieId: number | null;
  beschreibung: string;
  beteiligte: Beteiligter[];
  verweise: VerweisZeile[];
}

const istFamilienart = (art: Ereignisart) =>
  (FAMILIEN_EREIGNISARTEN as readonly string[]).includes(art);

const PERSONENARTEN = EREIGNISARTEN.filter((a) => !istFamilienart(a));

const name = (p: { vorname?: string; famname?: string }) =>
  `${p.vorname ?? ""} ${p.famname ?? ""}`.trim() || "ohne Namen";

/** Leere Maske für eine neue Eintragung. */
export function neuesEreignis(person: { id: number; vorname: string; famname: string }): EreignisWerte {
  return {
    art: "taufe",
    datumText: "",
    ortName: "",
    ortId: null,
    familieId: null,
    beschreibung: "",
    beteiligte: [{ personId: person.id, name: name(person), rolle: "hauptperson" }],
    verweise: [],
  };
}

/** Ein vorhandenes Ereignis aus der Personenansicht in die Maske übernehmen. */
export function ereignisAlsWerte(e: any): EreignisWerte {
  const datum: UnscharfesDatum | null =
    e.datumVon || e.datumText
      ? {
          von: e.datumVon ?? null,
          bis: e.datumBis ?? null,
          genauigkeit: e.datumGenauigkeit ?? "jahr",
          text: e.datumText ?? null,
        }
      : null;

  return {
    id: e.id,
    art: e.art,
    datumText: alsEingabetext(datum),
    ortName: e.ortName ?? "",
    ortId: e.ortId ?? null,
    familieId: e.familieId ?? null,
    beschreibung: e.beschreibung ?? "",
    beteiligte: (e.beteiligte ?? []).map((b: any) => ({
      personId: b.id ?? b.personId,
      name: name(b),
      rolle: b.rolle ?? "hauptperson",
    })),
    verweise: (e.verweise ?? []).map((v: any) => ({ titel: v.titel ?? "", url: v.url ?? "" })),
  };
}

const feld =
  "mt-1 w-full rounded-lg border border-black/15 bg-white px-3 py-2 outline-none " +
  "focus:border-black/40 dark:border-white/15 dark:bg-neutral-800";
const beschriftung = "text-sm text-black/60 dark:text-white/60";

export function EreignisFormular({
  start,
  person,
  familien,
  onFertig,
  onAbbrechen,
}: {
  start: EreignisWerte;
  person: { id: number; vorname: string; famname: string };
  /** Familien dieser Person, wie sie GET /api/personen/:id liefert. */
  familien: any[];
  onFertig: () => void;
  onAbbrechen: () => void;
}) {
  const [w, setW] = useState<EreignisWerte>(start);
  const [sucherOffen, setSucherOffen] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const [laeuft, setLaeuft] = useState(false);


  /** Familien, in denen die Person Elternteil ist — nur dort gibt es einen Partner. */
  const partnerfamilien = familien.filter((f) => f.eigeneRolle === "elternteil");
  const brauchtFamilie = istFamilienart(w.art);

  const setze = (teil: Partial<EreignisWerte>) => setW((alt) => ({ ...alt, ...teil }));

  /**
   * Beide Elternteile einer Familie als Beteiligte übernehmen.
   *
   * Als „Partner", nicht als Hauptperson: bei Geburt und Tod schreibt die API
   * das Datum in den Personendatensatz der Hauptperson zurück. Zwei
   * Hauptpersonen hieße dort ein Datum für zwei Menschen.
   */
  function familieWaehlen(familieId: number) {
    const f = partnerfamilien.find((x) => x.id === familieId);
    const eltern: Beteiligter[] = (f?.mitglieder ?? [])
      .filter((m: any) => m.rolle === "elternteil")
      .map((m: any) => ({
        personId: m.person.id,
        name: name(m.person),
        rolle: "partner" as Ereignisrolle,
      }));

    // Was darüber hinaus schon eingetragen war (Trauzeugen etwa), bleibt.
    const zusatz = w.beteiligte.filter((b) => !eltern.some((e) => e.personId === b.personId));
    setze({ familieId, beteiligte: [...eltern, ...zusatz] });
  }

  function artWaehlen(art: Ereignisart) {
    if (!istFamilienart(art)) {
      // Die automatisch gesetzten Partner gehören nicht zu einem Ereignis,
      // das nur eine Person betrifft - sonst stünde der Ehemann plötzlich
      // bei der Taufe seiner Frau in der Liste.
      const ohnePartner = w.beteiligte.filter((b) => b.rolle !== "partner");
      const beteiligte = ohnePartner.some((b) => b.personId === person.id)
        ? ohnePartner
        : [
            { personId: person.id, name: name(person), rolle: "hauptperson" as Ereignisrolle },
            ...ohnePartner,
          ];
      setze({ art, familieId: null, beteiligte });
      return;
    }
    setze({ art });
    // Nur eine Familie? Dann ist die Frage schon beantwortet.
    const einzige = partnerfamilien.length === 1 ? partnerfamilien[0] : null;
    if (w.familieId === null && einzige) familieWaehlen(einzige.id);
  }

  async function absenden(e: FormEvent) {
    e.preventDefault();
    setFehler(null);

    if (w.beteiligte.length === 0) {
      setFehler("Mindestens eine beteiligte Person.");
      return;
    }
    if (brauchtFamilie && w.familieId === null) {
      setFehler("Bitte die Familie wählen, die das betrifft.");
      return;
    }

    const verweise: VerweisZeile[] = [];
    for (const v of w.verweise) {
      const fertig = verweisAufbereiten(v);
      if (fertig === "ungueltig") {
        setFehler(`„${v.url}" ist keine brauchbare Adresse. Erlaubt sind http und https.`);
        return;
      }
      if (fertig) verweise.push(fertig);
    }

    const nutzlast = {
      art: w.art,
      datum: w.datumText.trim() ? parseDatum(w.datumText) : null,
      ortName: w.ortName.trim() || null,
      ortId: w.ortId,
      familieId: brauchtFamilie ? w.familieId : null,
      beschreibung: w.beschreibung.trim() || null,
      beteiligte: w.beteiligte.map((b) => ({ personId: b.personId, rolle: b.rolle })),
      verweise,
    };

    setLaeuft(true);
    try {
      if (w.id) await api.ereignisAendern(w.id, nutzlast);
      else await api.ereignisAnlegen(nutzlast);
      onFertig();
    } catch (f) {
      setFehler(f instanceof Error ? f.message : "Speichern fehlgeschlagen.");
    } finally {
      setLaeuft(false);
    }
  }

  return (
    <form
      onSubmit={absenden}
      className="space-y-4 rounded-lg border border-black/10 bg-black/[0.02] p-4 dark:border-white/10 dark:bg-white/5"
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <label className="block">
          <span className={beschriftung}>Was ist passiert?</span>
          <select
            value={w.art}
            onChange={(e) => artWaehlen(e.target.value as Ereignisart)}
            className={feld}
          >
            <optgroup label="eine Person betreffend">
              {PERSONENARTEN.map((a) => (
                <option key={a} value={a}>
                  {EREIGNIS_LABEL[a]}
                </option>
              ))}
            </optgroup>
            <optgroup label="beide Partner betreffend">
              {FAMILIEN_EREIGNISARTEN.map((a) => (
                <option key={a} value={a}>
                  {EREIGNIS_LABEL[a]}
                </option>
              ))}
            </optgroup>
          </select>
        </label>

        <DatumFeld beschriftung="Wann?" wert={w.datumText} onChange={(t) => setze({ datumText: t })} />

        <OrtFeld
          beschriftung="Wo?"
          id="ereignis-orte-vorschlaege"
          wert={w.ortName}
          onChange={(t) => setze({ ortName: t, ortId: null })}
          platzhalter="Ort, z.B. Krems an der Donau — Ebenen mit Komma"
          klasseFeld={feld}
          klasseBeschriftung={beschriftung}
        />

        <label className="block">
          <span className={beschriftung}>Anmerkung</span>
          {/*
            Mehrzeilig, weil hier auch einmal ein ganzer Matrikeneintrag
            landet. Die Spalte ist seit jeher `text()` und die API nimmt
            5000 Zeichen - eingeengt hat das nur das Eingabefeld.
          */}
          <textarea
            value={w.beschreibung}
            onChange={(e) => setze({ beschreibung: e.target.value })}
            maxLength={5000}
            rows={4}
            placeholder="optional — **fett**, *kursiv*, > Zitat und - Listen werden erkannt"
            className={feld}
          />
        </label>
      </div>

      {brauchtFamilie && (
        <div>
          <span className={beschriftung}>
            {EREIGNIS_LABEL[w.art]} betrifft beide Partner — welche Familie?
          </span>
          {partnerfamilien.length === 0 ? (
            <p className="mt-1 rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-800">
              {person.vorname} ist noch in keiner Familie Elternteil. Bitte zuerst
              unter „Familien“ eine anlegen und den Partner eintragen.
            </p>
          ) : (
            <div className="mt-2 space-y-1.5 text-sm">
              {partnerfamilien.map((f) => (
                <label key={f.id} className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="ereignis-familie"
                    checked={w.familieId === f.id}
                    onChange={() => familieWaehlen(f.id)}
                  />
                  <span>
                    {f.bezeichnung || "ohne Bezeichnung"}{" "}
                    <span className="text-black/40 dark:text-white/40">
                      (
                      {f.mitglieder
                        .filter((m: any) => m.rolle === "elternteil")
                        .map((m: any) => name(m.person))
                        .join(" & ")}
                      )
                    </span>
                  </span>
                </label>
              ))}
            </div>
          )}
        </div>
      )}

      <div>
        <span className={beschriftung}>Wer war beteiligt?</span>
        <ul className="mt-2 space-y-1.5">
          {w.beteiligte.map((b) => (
            <li key={b.personId} className="flex items-center gap-2 text-sm">
              <span className="w-44 shrink-0">{b.name}</span>
              <select
                value={b.rolle}
                onChange={(e) =>
                  setze({
                    beteiligte: w.beteiligte.map((x) =>
                      x.personId === b.personId
                        ? { ...x, rolle: e.target.value as Ereignisrolle }
                        : x,
                    ),
                  })
                }
                className="rounded-lg border border-black/15 bg-white px-2 py-1 text-sm
                           dark:border-white/15 dark:bg-neutral-800"
              >
                {EREIGNISROLLEN.map((r) => (
                  <option key={r} value={r}>
                    {EREIGNISROLLE_LABEL[r]}
                  </option>
                ))}
              </select>
              <button
                type="button"
                title="nicht beteiligt"
                onClick={() =>
                  setze({ beteiligte: w.beteiligte.filter((x) => x.personId !== b.personId) })
                }
                className="text-black/30 hover:text-red-700 dark:text-white/30"
              >
                ×
              </button>
            </li>
          ))}
        </ul>

        {sucherOffen ? (
          <div className="mt-2">
            <PersonSucher
              onWahl={(p) => {
                setSucherOffen(false);
                if (w.beteiligte.some((x) => x.personId === p.id)) return;
                setze({
                  beteiligte: [
                    ...w.beteiligte,
                    { personId: p.id, name: name(p), rolle: "beteiligt" },
                  ],
                });
              }}
              onAbbrechen={() => setSucherOffen(false)}
            />
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setSucherOffen(true)}
            className="mt-2 rounded-lg border border-black/15 px-3 py-1 text-sm
                       hover:border-black/40 dark:border-white/15"
          >
            + Person
          </button>
        )}
      </div>

      <div>
        <span className={beschriftung}>Verweise</span>
        <p className="text-xs text-black/40 dark:text-white/40">
          Wo steht das? Der Scan im Matricula-Archiv, ein Zeitungsartikel, ein
          Grabstein-Eintrag. Ohne Beschriftung wird der Rechnername genommen.
        </p>

        <ul className="mt-2 space-y-1.5">
          {w.verweise.map((v, i) => (
            <li key={i} className="flex items-center gap-2">
              <input
                value={v.titel}
                onChange={(e) =>
                  setze({
                    verweise: w.verweise.map((x, j) =>
                      j === i ? { ...x, titel: e.target.value } : x,
                    ),
                  })
                }
                maxLength={250}
                placeholder="Beschriftung"
                className="w-44 shrink-0 rounded-lg border border-black/15 bg-white px-2 py-1 text-sm
                           outline-none focus:border-black/40 dark:border-white/15 dark:bg-neutral-800"
              />
              <input
                value={v.url}
                onChange={(e) =>
                  setze({
                    verweise: w.verweise.map((x, j) =>
                      j === i ? { ...x, url: e.target.value } : x,
                    ),
                  })
                }
                maxLength={2000}
                inputMode="url"
                placeholder="https://…"
                className="flex-1 rounded-lg border border-black/15 bg-white px-2 py-1 text-sm
                           outline-none focus:border-black/40 dark:border-white/15 dark:bg-neutral-800"
              />
              <button
                type="button"
                title="Verweis entfernen"
                onClick={() => setze({ verweise: w.verweise.filter((_, j) => j !== i) })}
                className="text-black/30 hover:text-red-700 dark:text-white/30"
              >
                ×
              </button>
            </li>
          ))}
        </ul>

        {w.verweise.length < 20 && (
          <button
            type="button"
            onClick={() => setze({ verweise: [...w.verweise, { titel: "", url: "" }] })}
            className="mt-2 rounded-lg border border-black/15 px-3 py-1 text-sm
                       hover:border-black/40 dark:border-white/15"
          >
            + Verweis
          </button>
        )}
      </div>

      {fehler && (
        <p role="alert" className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-800">
          {fehler}
        </p>
      )}

      <div className="flex gap-2">
        <button
          type="submit"
          disabled={laeuft}
          className="rounded-lg bg-tinte px-4 py-2 text-white disabled:opacity-50
                     dark:bg-white dark:text-tinte"
        >
          {laeuft ? "…" : w.id ? "Änderungen speichern" : "Ereignis eintragen"}
        </button>
        <button
          type="button"
          onClick={onAbbrechen}
          className="rounded-lg border border-black/15 px-4 py-2 dark:border-white/15"
        >
          Abbrechen
        </button>
      </div>
    </form>
  );
}
